import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { UserFilters, UserRole, SortBy, SortOrder } from '../types/user.types';

// Debounce delay in milliseconds
const DEBOUNCE_DELAY = 300;

interface UseUserFiltersReturn {
  filters: UserFilters;
  setSearch: (search: string) => void;
  setRole: (role: UserRole | 'all') => void;
  setStatus: (status: 'active' | 'blocked' | 'all') => void;
  setSortBy: (sortBy: SortBy) => void;
  setSortOrder: (sortOrder: SortOrder) => void;
  setPage: (page: number) => void;
  resetFilters: () => void;
}

export function useUserFilters(): UseUserFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Read initial state from URL
  const search = searchParams.get('search') || '';
  const role = (searchParams.get('role') as UserRole | 'all') || 'all';
  const status = (searchParams.get('status') as 'active' | 'blocked' | 'all') || 'all';
  const sortBy = (searchParams.get('sortBy') as SortBy) || 'createdAt';
  const sortOrder = (searchParams.get('sortOrder') as SortOrder) || 'desc';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = parseInt(searchParams.get('limit') || '50', 10);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [search]);

  // Update URL with new params
  const updateParams = useCallback(
    (updates: Partial<Record<string, string>>) => {
      const newParams = new URLSearchParams(searchParams);

      Object.entries(updates).forEach(([key, value]) => {
        if (value && value !== 'all' && value !== '') {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const setSearch = useCallback(
    (newSearch: string) => {
      updateParams({ search: newSearch, page: '1' });
    },
    [updateParams]
  );

  const setRole = useCallback(
    (newRole: UserRole | 'all') => {
      updateParams({ role: newRole, page: '1' });
    },
    [updateParams]
  );

  const setStatus = useCallback(
    (newStatus: 'active' | 'blocked' | 'all') => {
      updateParams({ status: newStatus, page: '1' });
    },
    [updateParams]
  );

  const setSortBy = useCallback(
    (newSortBy: SortBy) => {
      updateParams({ sortBy: newSortBy, page: '1' });
    },
    [updateParams]
  );

  const setSortOrder = useCallback(
    (newSortOrder: SortOrder) => {
      updateParams({ sortOrder: newSortOrder });
    },
    [updateParams]
  );

  const setPage = useCallback(
    (newPage: number) => {
      updateParams({ page: newPage.toString() });
    },
    [updateParams]
  );

  const resetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Build filters object for API
  const filters: UserFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      role: role !== 'all' ? role : undefined,
      status: status !== 'all' ? status : undefined,
      sortBy,
      sortOrder,
      page,
      limit,
    }),
    [debouncedSearch, role, status, sortBy, sortOrder, page, limit]
  );

  return {
    filters,
    setSearch,
    setRole,
    setStatus,
    setSortBy,
    setSortOrder,
    setPage,
    resetFilters,
  };
}
